import { useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, Play, Newspaper, Calendar, Mic, PenSquare, HeartHandshake, Megaphone } from "lucide-react"; // Added new icons
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/hooks/use-toast";
// Removed Navbar and Footer imports as they are handled by PublicLayout
import NewsletterSignup from "@/components/ui/NewsletterSignup";

interface Campaign {
  id: string;
  title: string;
  description: string;
  image: string;
  link: string;
}
interface MediaItem {
  id: string;
  description: string;
  image: string;
  link: string;
  tag: "Video" | "Artikel" | "Event" | "Ljud";
}
const campaigns: Campaign[] = [{
  id: "freddy-cycles",
  title: "Freddy cyklar",
  description: "Freddy fick Alzheimer 54 år ung, han cyklar runt vättern för att öka kunskapen om Alzheimer.",
  image: "/images/kampanj-freddy.webp",
  link: "#freddy-campaign"
}, {
  id: "young-affected",
  title: "Unga drabbade",
  description: "Kampanjen som visar hur det är att vara anhörig när man själv är barn, tonåring eller ung vuxen.",
  image: "/images/kampanj-ungadrabbde.webp",
  link: "#young-affected"
}];
const mediaItems: MediaItem[] = [{
  id: "svt",
  description: "Ny alzheimermedicin ger dem hoppet tillbaka: \"Fantastiskt\"",
  image: "/images/logos/svt-logo.png",
  link: "https://www.sverigesradio.se/artikel/ny-alzheimermedicin-ger-dem-hoppet-tillbaka-fantastiskt",
  tag: "Ljud"
}, {
  id: "padel",
  description: "Padelturnerning i Nyköping samlar in 25 000 kr för Alzheimer",
  image: "/images/logos/padel-event.png",
  link: "https://www.svt.se/nyheter/lokalt/sormland/fredrik-drabbades-av-alzheimers-mitt-i-livet-trodde-inte-pa-det",
  tag: "Event"
}, {
  id: "svd",
  description: "SVD: Jessica, 55: Allt över tio bra år till är en bonus",
  image: "/images/logos/svd-logo.png",
  link: "https://www.svd.se/a/AvJ3mz/leqembi-ny-medicin-mot-alzheimer-ger-hopp-grat-av-lycka",
  tag: "Artikel"
}, {
  id: "podcast",
  description: "Värdefulla poddar från Alzheimer Life",
  image: "/images/logos/hjarnpodden-logo.png",
  link: "https://open.spotify.com/show/7H2n34uKAGkhJ6fkff60Lt",
  tag: "Ljud"
}];
const getTagIcon = (tag: MediaItem["tag"]) => {
  switch (tag) {
    case "Video":
      return <Play className="w-4 h-4" />;
    case "Artikel":
      return <Newspaper className="w-4 h-4" />;
    case "Event":
      return <Calendar className="w-4 h-4" />;
    case "Ljud":
      return <Mic className="w-4 h-4" />;
    default:
      return null;
  }
};
const getEmojiForTag = (tag: MediaItem["tag"]) => {
  switch (tag) {
    case "Video":
      return "🎥";
    case "Artikel":
      return "🗞";
    case "Event":
      return "🎾";
    case "Ljud":
      return "🎙";
    default:
      return "";
  }
};
const Kampanjer = () => {
  // Removed outer div and Navbar, handled by layout
  return <>
      <section className="flex-grow"> {/* Removed min-h-screen and flex-col */}
        {/* Hero Section */}
        <div className="relative h-[600px] mb-16">
          <div className="absolute inset-0">
            <img src="/images/kampanjer/ai-gen-research02.webp" alt="Kampanjer & berättelser" className="w-full h-full object-cover brightness-50" />
          </div>
          <div className="relative h-full flex flex-col items-center justify-center text-center text-white container mx-auto px-4">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Kampanjer & berättelser
            </h1>
            <p className="text-xl md:text-2xl max-w-3xl">
              Vi lyfter röster och driver initiativ som förändrar. Här hittar du våra egna kampanjer – och media där våra hjältar synts och påverkat.
            </p>
          </div>
        </div>

        {/* Kampanjer Section */}
        <section id="kampanjer" className="mb-16">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-minnestig-purple mb-12 text-center">
              Pågående & genomförda kampanjer
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Kampanj 1 */}
              <div className="bg-white rounded-[24px] shadow-lg overflow-hidden">
                <img src="/images/kampanjer/freddy-cyklar-ai.webp" alt="Freddy cyklar" className="w-full h-[300px] object-cover" />
                <div className="p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">
                    Freddy cyklar
                  </h3>
                  <p className="text-gray-700 mb-6">Freddy fick Alzheimer 54 år ung, nu cyklar han runt Vättern för alla som lever med demens, för att visa att livet inte tar slut med en diagnos. </p>
                  <Link to="/kampanjer/freddy-cyklar" className="text-minnestig-purple hover:text-minnestig-teal inline-flex items-center">
                    <span className="mr-2">Läs mer</span>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                    </svg>
                  </Link>
                </div>
              </div>

              {/* Kampanj 2 */}
              <div className="bg-white rounded-[24px] shadow-lg overflow-hidden">
                <img src="/images/kampanjer/kampanj-ungadrabbde.webp" alt="Unga drabbade" className="w-full h-[300px] object-cover" />
                <div className="p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">
                    Unga drabbade
                  </h3>
                  <p className="text-gray-700 mb-6">Kampanjen lyfter de 10 000 unga drabbade av demens under 65 år. För det kunde varit du eller någon du älskar. </p>
                  <a href="https://www.youtube.com/watch?v=-LfalhPOHX4" target="_blank" rel="noopener noreferrer" className="text-minnestig-purple hover:text-minnestig-teal inline-flex items-center">
                    <span className="mr-2">Se klippet</span>
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Media Section */}
        <section id="media" className="mb-16">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-minnestig-purple mb-12 text-center">
              Demenshjältar och våra vänner i media
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {mediaItems.map(item => <a key={item.id} href={item.link} target="_blank" rel="noopener noreferrer" className="block transition-transform hover:scale-[1.02]">
                  <Card className="bg-white rounded-[24px] shadow-lg overflow-hidden h-full">
                    <CardContent className="p-6">
                      <div className="flex flex-col">
                        <div className="flex items-center gap-2 mb-2">
                          {getTagIcon(item.tag)}
                          <span className="text-minnestig-purple">{item.tag}</span>
                          <ExternalLink className="w-4 h-4 text-gray-400" />
                        </div>
                        <h3 className="text-xl font-semibold text-gray-900">
                          {item.description}
                        </h3>
                      </div>
                    </CardContent>
                  </Card>
                </a>)}
            </div>
          </div>
        </section>

        {/* Engagera Section */}
        <section id="engagera" className="w-full bg-[#F8F2FF] py-24">
          <div className="container mx-auto px-4">
            <h2 className="text-4xl font-bold text-minnestig-purple mb-4 text-center">
              Vill du bli en Demenshjälte?
            </h2>
            <p className="text-xl text-gray-700 mb-16 max-w-3xl mx-auto text-center">
              Oavsett om du vill dela din berättelse, arrangera en aktivitet eller sprida våra kampanjer – vi behöver dig. Tillsammans gör vi skillnad.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {/* CTA Card 1 */}
              {/* CTA Card 1 */}
              <div className="bg-white p-8 rounded-[24px] shadow-lg">
                <div className="flex flex-col items-center text-center h-full justify-between">
                  <div>
                    {/* Updated Icon */}
                    <div className="mb-4 rounded-full bg-minnestig-lavender/30 w-12 h-12 flex items-center justify-center mx-auto">
                      <PenSquare className="text-minnestig-purple" size={24} />
                    </div>
                    <h3 className="text-2xl font-bold mb-2">Dela din story</h3>
                    <p className="text-gray-700 mb-8">
                      Skicka in din berättelse – vi hjälper dig att nå ut.
                    </p>
                  </div>
                  <button disabled className="w-full bg-gray-400 text-white py-4 px-6 rounded-full font-medium cursor-not-allowed">
                    Kommer snart
                  </button>
                </div>
              </div>

              {/* CTA Card 2 */}
              {/* CTA Card 2 */}
              <div className="bg-white p-8 rounded-[24px] shadow-lg">
                <div className="flex flex-col items-center text-center h-full justify-between">
                  <div>
                    {/* Updated Icon */}
                    <div className="mb-4 rounded-full bg-minnestig-peach/30 w-12 h-12 flex items-center justify-center mx-auto">
                      <HeartHandshake className="text-minnestig-purple" size={24} />
                    </div>
                    <h3 className="text-2xl font-bold mb-2">Starta en insamling eller aktivitet</h3>
                    <p className="text-gray-700 mb-8">
                      Ordna något lokalt eller digitalt – vi ger dig verktygen.
                    </p>
                  </div>
                  <button disabled className="w-full bg-gray-400 text-white py-4 px-6 rounded-full font-medium cursor-not-allowed">
                    Kommer snart
                  </button>
                </div>
              </div>

              {/* CTA Card 3 */}
              {/* CTA Card 3 */}
              <div className="bg-white p-8 rounded-[24px] shadow-lg">
                <div className="flex flex-col items-center text-center h-full justify-between">
                  <div>
                    {/* Updated Icon */}
                    <div className="mb-4 rounded-full bg-minnestig-lavender/30 w-12 h-12 flex items-center justify-center mx-auto">
                      <Megaphone className="text-minnestig-purple" size={24} />
                    </div>
                    <h3 className="text-2xl font-bold mb-2">Sprid vårt budskap i sociala medier</h3>
                    <p className="text-gray-700 mb-8">
                      Ladda ner material och tagga oss.
                    </p>
                  </div>
                  <button disabled className="w-full bg-gray-400 text-white py-4 px-6 rounded-full font-medium cursor-not-allowed">
                    Kommer snart
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </section>
      
      <NewsletterSignup />
      {/* Removed Footer, handled by layout */}
    </>;
};
export default Kampanjer;
